import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, User, Mail, Building2, MapPin, Calendar, FileText, Link, Loader2, Upload, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { submitSpotlight, createSpotlightCheckout, uploadLogo } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SpotlightPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    location: '',
    link: '',
    contact_name: '',
    contact_email: '',
    business_name: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.event_date || !formData.contact_name || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);

    try {
      let imageFileId = null;
      if (imageFile) {
        try {
          const uploadRes = await uploadLogo(imageFile);
          imageFileId = uploadRes.data.file_id;
        } catch { toast.error('Image upload failed, continuing without image'); }
      }

      const res = await submitSpotlight({ ...formData, image_file_id: imageFileId });
      const spotlightId = res.data.id;

      const checkoutRes = await createSpotlightCheckout(spotlightId, window.location.origin);
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      console.error('Spotlight submission error:', error);
      toast.error(error.response?.data?.detail || 'Submission failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOPage.Register />
      <Navbar />

      <section className="pt-32 pb-16 bg-gradient-to-b from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            <Sparkles className="h-3 w-3 mr-1" /> Event Spotlight
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Promote Your Event
          </h1>
          <p className="text-teal-100 text-lg max-w-2xl mx-auto">
            Get your event featured at the top of our Events page, promoted on the Paisley.org.uk blog and across all social media channels.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white/10 rounded-full px-6 py-3">
            <span className="text-3xl font-bold">£30</span>
            <span className="text-teal-200 text-sm">one-off &middot; 30 days promotion</span>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: '1', title: 'Featured Placement', desc: 'Your event pinned at the top of the Events page with a Sponsored badge' },
              { icon: '2', title: 'Blog Feature', desc: 'Dedicated promotion on the Paisley.org.uk website' },
              { icon: '3', title: 'Social Media Push', desc: 'Shared across all Paisley.org.uk social media channels' },
            ].map((item) => (
              <Card key={item.icon} className="text-center border-teal-100" data-testid={`benefit-${item.icon}`}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-teal-600">{item.icon}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="shadow-lg" data-testid="spotlight-form-card">
            <CardHeader>
              <CardTitle className="text-xl">Event Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><FileText className="h-4 w-4 text-slate-400" /> Event Title *</Label>
                    <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Paisley Food Festival" required data-testid="spotlight-title" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Calendar className="h-4 w-4 text-slate-400" /> Event Date *</Label>
                    <Input name="event_date" value={formData.event_date} onChange={handleChange} placeholder="e.g. Saturday 15 June 2026" required data-testid="spotlight-date" />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><FileText className="h-4 w-4 text-slate-400" /> Description *</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange} placeholder="Tell people about your event..." rows={4} required data-testid="spotlight-desc" />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><MapPin className="h-4 w-4 text-slate-400" /> Location *</Label>
                    <Input name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Paisley Town Hall" required data-testid="spotlight-location" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Link className="h-4 w-4 text-slate-400" /> Event Link</Label>
                    <Input name="link" value={formData.link} onChange={handleChange} placeholder="https://..." data-testid="spotlight-link" />
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><Image className="h-4 w-4 text-slate-400" /> Event Image</Label>
                  <div className="flex items-center gap-4">
                    {imagePreview ? (
                      <div className="relative w-24 h-24 rounded-lg border overflow-hidden bg-white">
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center" data-testid="remove-image-btn">x</button>
                      </div>
                    ) : (
                      <label htmlFor="spotlight-image" className="flex flex-col items-center justify-center w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 cursor-pointer hover:border-teal-400 transition-colors bg-slate-50" data-testid="image-upload-area">
                        <Upload className="h-5 w-5 text-slate-400" />
                        <span className="text-xs text-slate-400 mt-1">Upload</span>
                      </label>
                    )}
                    <input id="spotlight-image" type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-testid="spotlight-image-input" />
                    <p className="text-sm text-slate-500">Optional. JPEG, PNG, WebP. Max 5MB.</p>
                  </div>
                </div>

                <hr />

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><User className="h-4 w-4 text-slate-400" /> Your Name *</Label>
                    <Input name="contact_name" value={formData.contact_name} onChange={handleChange} required data-testid="spotlight-contact-name" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Mail className="h-4 w-4 text-slate-400" /> Your Email *</Label>
                    <Input name="contact_email" type="email" value={formData.contact_email} onChange={handleChange} required data-testid="spotlight-contact-email" />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><Building2 className="h-4 w-4 text-slate-400" /> Business/Organisation Name</Label>
                  <Input name="business_name" value={formData.business_name} onChange={handleChange} data-testid="spotlight-business" />
                </div>

                <Button type="submit" disabled={submitting} className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white text-base" data-testid="spotlight-submit-btn">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</> : <>Pay £30 & Submit Event <Sparkles className="h-4 w-4 ml-2" /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SpotlightPage;
